/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Heading } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import GetInternationalWineLocation, { GetWineInternationalLocationInput, IGetWineInternationalDistributorOuput, IGetWineInternationalLocationOutput, IGetWineInternationalQueryFilters } from './actions/get-wine-international-location';
import GetWineLocation, { GetWineLocationInput, IGetWineLocationDataOutput, IGetWineLocationQueryFilters, IGetWineLocationRetailerOutput } from './actions/get-wine-location';
import getWineProducts, { GetWineProductsInput, IGetWineProductQueryFilters, IGetWineProductsData } from './actions/get-wine-products';
import { IWineFinderFormConfig, IWineFinderFormProps } from './wine-finder-form.props.autogenerated';
import WineFinderQueryLocation from './wine-finder-query-location';
import WineFinderQueryLocationInternational from './wine-finder-query-location-international';
import { WineFinderScrollable } from './wine-finder-scrollable';
import WineFinderResults from './wine-finder-wine-results';

// =============================================================================
// INTERFACES AND CONSTANTS
// =============================================================================
const enum selectedTab {
    us = 'us',
    international = 'international'
}
export interface IExpandedState{
    expandedState:boolean;
}

/**
 * GetWine component: Component responsible for displaying local wineries based
 * on the query input
 * @extends {React.Component<IWineFinderFormProps<IWineFinderFormConfig>>}
 */
@observer
class FindWine extends React.Component<IWineFinderFormProps<IWineFinderFormConfig>, IExpandedState> {


    // =========================================================================
    // OBSERVABLES AND FIELDS
    // =========================================================================

    /**
     * manages which type of page is supposed to be currently rendered
     *
     * - query: when _pageState is query the search page, will be rendered
     * - results: when _pageState is results a list of results will be rendered
     */
    @observable private _pageState: 'query' | 'results' = 'query';

    /**
     * keep the state of the 'Show All Products' toggle
     */
    @observable private _allOpen: boolean = false;

    /**
     * List of wine products
     */
    @observable private _products: IGetWineProductsData[] | undefined;

    /**
     * contains data on the previous domestic wine locations api request
     */
    @observable private wineLocation: IGetWineLocationDataOutput | undefined;

    /**
     * Indicates which is the current selected tab
     */
    @observable private _selectedTab: string = 'us';

    private _wineLocationDataSet: (IGetWineInternationalDistributorOuput | IGetWineLocationRetailerOutput)[] = [];

    /**
     * contains data on the previous international wine locations api request
     */
    @observable private internationalWineLocation: IGetWineInternationalLocationOutput | undefined;

    /**
     * the filters previously used for the last api request
     */
    private _querySearchFilters: IGetWineLocationQueryFilters | IGetWineInternationalQueryFilters = {
        country: 'all',
        location: '',
        brand: this.props.config.brand
    };

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================

    public constructor(props: IWineFinderFormProps<IWineFinderFormConfig>) {
        super(props);

        // bindings for all functions used within the render
        this._setWineLocation = this._setWineLocation.bind(this);
        this._setWineProducts = this._setWineProducts.bind(this);
        this._handleBackToFind = this._handleBackToFind.bind(this);
        this._setInternationalWineLocation = this._setInternationalWineLocation.bind(this);
        this._handlePageChange = this._handlePageChange.bind(this);
        this._getMoreWineLocations = this._getMoreWineLocations.bind(this);
        this._closeToggle = this._closeToggle.bind(this);
        this._selectUSTab = this._selectUSTab.bind(this);
        this._selectInternationalTab = this._selectInternationalTab.bind(this);

        // safeguard product list
        reaction(
            () => { return this._products; },
            () => { this.setState({}); }
        );
        this.state={
            expandedState:false
        };
    }

    public componentDidMount(): void {
        if (this.props.config.showChooseWineInput) {
            void this._setWineProducts();
        }
    }

    public render(): JSX.Element {
        if (this.props.config.singlePage) {
            return (
                <div className='wine-finder-form-single-page'>
                    {this._renderQuery()}
                    {this._pageState === 'results' && this._renderResults()}
                </div>
            );
        }

        if (this._pageState === 'query') {
            return this._renderQuery();
        } else {
            return this._renderJSXResult();
        }
    }

    // ==========================================================================
    // JSX CONSTRUCTORS
    // ==========================================================================

    private _renderJSXResult(): JSX.Element {
        // if we are rendering retailer and the search returned nothing,
        // display a special error page stating no results were found
        const wineType = (this.wineLocation) ? 'retailer' : 'distributor';
        if (wineType === 'retailer' && this.wineLocation && this.wineLocation.data.length === 0) {
            return this._renderNoResults();
        }
        return this._renderResults();
    }

    // Select US tab with US search form
    private _selectUSTab(): void {
        this._selectedTab = 'us';
    }

    // Select International tab with International search form
    private _selectInternationalTab(): void {
        this._selectedTab = 'international';
    }

    private _renderQuery(): JSX.Element {
        const {
            hideInternational,
            searchRadius,
            showFilterButtons,
            placeholders,
            USLabelText,
            internationalLabelText
        } = this.props.config;
        const USCssClass = this._selectedTab === selectedTab.us?'find-wine-tab-us active':'find-wine-tab-us';
        const internationalCssClass = this._selectedTab === selectedTab.international?'find-wine-tab-international active':'find-wine-tab-international';
        return (
            <div className='find-wine'>
                <div className='find-wine-container'>
                    <div className='find-wine-info-text'>
                        {this._title}
                        {this._searchText}
                    </div>

                    {
                        !hideInternational &&
                        <div className='find-wine-tab'>
                            <button className={USCssClass} onClick={this._selectUSTab}>
                                {USLabelText}
                            </button>
                            <button className={internationalCssClass} onClick={this._selectInternationalTab}>
                                {internationalLabelText}
                            </button>
                        </div>
                    }

                    <WineFinderQueryLocation
                        defaultRadius={searchRadius}
                        displayFilters={showFilterButtons}
                        onQuerySearch={this._setWineLocation}
                        resources={this.props.resources}
                        placeholders={placeholders}
                        config={this.props.config}
                        products={this._products}
                        active={this._selectedTab === selectedTab.us}
                    />
                    {
                        !hideInternational &&
                        <WineFinderQueryLocationInternational
                            resources={this.props.resources}
                            actionContext={this.props.context.actionContext}
                            onQuerySearch={this._setInternationalWineLocation}
                            submitText={this.props.config.wineFinderSubmit}
                            active={this._selectedTab === selectedTab.international}
                        />
                    }
                </div>
            </div>
        );
    }

    /**
     * JSX for the result page
     */
    private _renderResults(): JSX.Element {
        const { wineFinder__backToFind, wineFinder__showMoreButton } = this.props.resources;
        const wineType = (this.wineLocation) ? 'retailer' : 'distributor';
        const wineLocation = (this.wineLocation) ? this.wineLocation : this.internationalWineLocation;
        const resultDisplay = (this.props.config.resultsDisplay === 'paginate') ?
            (
                <WineFinderResults
                    config={this.props.config}
                    resultType={wineType}
                    onPageChange={this._handlePageChange}
                    queryFilters={this._querySearchFilters}
                    wineLocation={wineLocation as IGetWineLocationDataOutput | IGetWineInternationalLocationOutput}
                    resources={this.props.resources}
                    allOpen={this._allOpen}
                    hideNumbers={this.props.config.hideNumbers || false}
                />
            ) :
            (
                <WineFinderScrollable
                    config={this.props.config}
                    showMoreText={wineFinder__showMoreButton}
                    resultType={wineType}
                    onGetMoreContent={this._getMoreWineLocations}
                    wineResults={this._wineLocationDataSet}
                    queryFilters={this._querySearchFilters}
                    wineLocationData={wineLocation as IGetWineLocationDataOutput | IGetWineInternationalLocationOutput}
                    resources={this.props.resources}
                    allOpen={this._allOpen}
                    hideNumbers={this.props.config.hideNumbers || false}
                />
            );
        return (
            <div className='find-wine-result'>
                <div className='find-wine-result-container'>
                    {
                        !this.props.config.singlePage &&
                        <a className='find-wine-result-back-link' tabIndex={0} onClick={this._handleBackToFind} onKeyPress={this._handleBackToFind}>{wineFinder__backToFind}</a>
                    }
                    {this.props.config.showAllProductsToggle && this._renderAllProductsToggle()}
                    {resultDisplay}
                </div>
            </div>
        );
    }

    /**
     * JSX for the 'Show All Products' toggle
     */
    private _renderAllProductsToggle(): JSX.Element {
        const {
            hideAllProducts,
            showAllProducts
        } = this.props.config.showAllProductsLabel || {hideAllProducts:'Hide All Products', showAllProducts:'Show All Products'};
        return (
            <div className='find-wine-result-show-all'>
                <a className='find-wine-result-show-all-toggle' tabIndex={0} onClick={this._closeToggle} aria-expanded={this.state.expandedState} onKeyPress={this._closeToggle}>
                    {this._allOpen ? hideAllProducts : showAllProducts}
                </a>
            </div>
        );
    }

    private _closeToggle(): void {
        this._allOpen = !this._allOpen;
        this.setState({
            expandedState: !this.state.expandedState
        });
    }

    /**
     * JSX for results failed page
     *
     * this is only for retailer / domestic search
     */
    private _renderNoResults(): JSX.Element {
        const {
            wineFinder__backToFind,
            wineFinder__noResults,
            wineFinder__searchAgain
        } = this.props.resources;
        return (
            <div className='find-wine-no-result'>
                <div className='find-wine-no-result-container'>
                    <a role='button' className='find-wine-no-result-back-link' onClick={this._handleBackToFind}>{wineFinder__backToFind}</a>
                    <div className='find-wine-no-result-title'>
                        {this._title}
                        <p>{wineFinder__noResults}</p>
                        <button className='find-wine-no-result-search-again' onClick={this._handleBackToFind}>{wineFinder__searchAgain}</button>
                    </div>
                </div>
            </div>
        );
    }

    private get _title(): JSX.Element | null {
        const title = this.props.config.title;
        if (title && title.text) {
            return (
                <Heading
                    {...title}
                    headingTag={title.tag}
                    className='find-wine-title'
                />
            );
        }
        return null;
    }

    private get _searchText(): JSX.Element | null {
        const { searchText } = this.props.config;
        if (searchText) {
            return (
                // tslint:disable-next-line:react-no-dangerous-html
                <RichTextComponent
                    className='find-wine-search-text'
                    text={searchText}
                />
            );
        }

        return null;
    }

    /**
     * return back to query page
     */
    private _handleBackToFind(): undefined {
        this._pageState = 'query';
        return undefined;
    }

    /**
     * handles a re-request to change pages
     *
     * @param filters query filter for either domestic or international searching
     */
    private async _handlePageChange(filters: IGetWineLocationQueryFilters | IGetWineInternationalQueryFilters): Promise<undefined> {
        if (this.wineLocation) {
            await this._setWineLocation(filters as IGetWineLocationQueryFilters);
        } else {
            await this._setInternationalWineLocation(filters as IGetWineInternationalQueryFilters);
        }
        return undefined;
    }

    private async _getMoreWineLocations(filters: IGetWineLocationQueryFilters | IGetWineInternationalQueryFilters): Promise<void> {
        if (!filters.page) {
            filters.page = 2;
        } else {
            filters.page++;
        }
        if (this.wineLocation) {
            (filters as IGetWineLocationQueryFilters).brand = this.props.config.brand;
            const wineLocationInput = new GetWineLocationInput(filters as IGetWineLocationQueryFilters);
            const moreData = await GetWineLocation(wineLocationInput, this.props.context.actionContext);
            this._wineLocationDataSet = [...this._wineLocationDataSet, ...moreData.data];
        } else {
            (filters as IGetWineInternationalQueryFilters).brand_code = this.props.config.brand;
            const wineLocationInput = new GetWineInternationalLocationInput(filters as IGetWineInternationalQueryFilters);
            const moreData = await GetInternationalWineLocation(wineLocationInput, this.props.context.actionContext);
            this._wineLocationDataSet = [...this._wineLocationDataSet, ...moreData.data];
        }
        this.setState({});
    }

    // =========================================================================
    // DATA ACTIONS AND QUERIES
    // =========================================================================

    /**
     * runs the users data query and applies the new results to the component
     *
     * @param filters query filters for the call
     */
    private async _setWineLocation(filters: IGetWineLocationQueryFilters): Promise<IGetWineLocationDataOutput> {
        this._allOpen = false;
        filters.brand = this.props.config.brand;
        filters.per_page = this.props.config.perPage || 5;
        const wineLocationInput = new GetWineLocationInput(filters);
        this._querySearchFilters = filters;
        const newLocation = await GetWineLocation(wineLocationInput, this.props.context.actionContext);
        this._wineLocationDataSet = newLocation.data;
        this.wineLocation = newLocation;
        // must be set to undefined to keep which was previous call consistent
        this.internationalWineLocation = undefined;
        this._pageState = 'results';
        return this.wineLocation;
    }

    /**
     * runs the users data query and applies the new results to the component
     *
     * @param filters query filters for the call
     */
    private async _setInternationalWineLocation(filters: IGetWineInternationalQueryFilters): Promise<IGetWineInternationalLocationOutput> {
        filters.brand_code = this.props.config.brand;
        filters.per_page = this.props.config.perPage || 5;
        const wineLocationInput = new GetWineInternationalLocationInput(filters);
        const newLocation = await GetInternationalWineLocation(wineLocationInput, this.props.context.actionContext);
        this._wineLocationDataSet = newLocation.data;
        this.internationalWineLocation = newLocation;
        this._querySearchFilters = filters;
        // must be set to undefined to keep which was previous call consistent
        this.wineLocation = undefined;
        this._pageState = 'results';
        return this.internationalWineLocation;
    }

    /**
     * get a list of products
     *
     * @param filters query filters for the call
     */
    private async _setWineProducts(): Promise<void> {
        const filter: IGetWineProductQueryFilters = {brand: this.props.config.brand};
        const wineLocationInput = new GetWineProductsInput(filter);
        const products = await getWineProducts(wineLocationInput, this.props.context.actionContext);
        this._products = products;
    }
}

export default FindWine;
